import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useRef, useState, type FormEvent } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Logo } from "@/components/Logo";
import {
  checkLoginAllowed,
  recordLoginFailure,
  clearLoginAttempts,
} from "@/lib/login-guard.functions";
import {
  TurnstileWidget,
  type TurnstileHandle,
} from "@/components/security/TurnstileWidget";



const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email("Bitte gültige E-Mail-Adresse eingeben").max(255),
  password: z.string().min(6, "Mindestens 6 Zeichen").max(72),
});

const GENERIC_ERROR = "Anmeldung fehlgeschlagen. Bitte E-Mail und Passwort prüfen.";

function formatLockMessage(lockedUntilIso: string): string {
  const remainingMs = new Date(lockedUntilIso).getTime() - Date.now();
  const minutes = Math.max(1, Math.ceil(remainingMs / 60_000));
  return `Zu viele Fehlversuche. Bitte in ca. ${minutes} Minute${minutes === 1 ? "" : "n"} erneut versuchen.`;
}

export const Route = createFileRoute("/cockpit_/login")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Cockpit-Login – creategy" },
      { name: "description", content: "Anmeldung zum persönlichen creategy-Cockpit." },
      { property: "og:title", content: "Cockpit-Login – creategy" },
      { property: "og:description", content: "Anmeldung zum persönlichen creategy-Cockpit." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  validateSearch: (
    search: Record<string, unknown>,
  ): { redirect?: string; email?: string; forgot?: boolean } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : "/cockpit",
    email: typeof search.email === "string" ? search.email.toLowerCase() : undefined,
    forgot: search.forgot === "1" || search.forgot === true ? true : undefined,
  }),

  beforeLoad: async ({ search }) => {
    // Feature-Flag: solange „cockpit_public_visible" AUS ist, ist die Login-
    // Seite für Besucher nicht erreichbar. Preview-Umgebungen umgehen den
    // Flag-Redirect, damit intern getestet werden kann.
    const { supabase: sb } = await import("@/integrations/supabase/client");
    const { data } = await sb.auth.getUser();
    if (!data.user) {
      const host = typeof window !== "undefined" ? window.location.hostname : "";
      const isPreview =
        host.endsWith(".lovable.dev") ||
        host.endsWith(".lovableproject.com") ||
        host.includes("id-preview--") ||
        host === "localhost" ||
        host === "127.0.0.1";
      if (!isPreview) {
        const { getPublicFeatureFlag } = await import("@/lib/feature-flags.functions");
        // Ticket AD1a: nur dieses eine Flag, keine Liste. Eine Abweisung gilt
        // wie „aus".
        const flag = await getPublicFeatureFlag({
          data: { key: "cockpit_public_visible" },
        }).catch(() => ({ enabled: false }));
        if (!flag.enabled) {
          throw redirect({ to: "/" });
        }
      }
      return;
    }
    const email = (data.user.email ?? "").toLowerCase();
    if (email.endsWith("@creategy.de")) {
      // Interne Konten haben im Cockpit nichts verloren.
      throw redirect({ to: "/admin" });
    }
    throw redirect({ to: search.redirect });
  },
  component: CockpitLoginPage,
});

function CockpitLoginPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [email, setEmail] = useState(search.email ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  // Kommt der Nutzer über „Neuen Link anfordern", direkt auf den Reset-Weg hinweisen.
  const [info, setInfo] = useState<string | null>(
    search.forgot
      ? "Ihr Link ist abgelaufen oder verbraucht. Bitte fordern Sie unter „Passwort vergessen?“ einen neuen Link an."

      : null,
  );

  const turnstileRef = useRef<TurnstileHandle>(null);

  const resetTurnstile = () => {
    turnstileRef.current?.reset();
    setTurnstileToken(null);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    const parsed = loginSchema.safeParse({ email, password });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Eingabe ungültig");
      return;
    }
    if (!turnstileToken) {
      setError("Bitte Bot-Schutz-Prüfung abschließen.");
      return;
    }
    setBusy(true);
    try {
      const normalizedEmail = parsed.data.email.toLowerCase();

      const gate = await checkLoginAllowed({
        data: { email: normalizedEmail, turnstileToken, area: "cockpit" },
      });
      resetTurnstile();
      if (!gate.allowed) {
        if (gate.reason === "turnstile") {
          setError("Bot-Schutz konnte nicht bestätigt werden. Bitte erneut versuchen.");
        } else if (gate.lockedUntil) {
          setError(formatLockMessage(gate.lockedUntil));
        } else {
          setError(GENERIC_ERROR);
        }
        return;
      }

      const { error: authErr } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password: parsed.data.password,
      });

      if (authErr) {
        const failure = await recordLoginFailure({
          data: { email: normalizedEmail, area: "cockpit" },
        }).catch(() => null);
        if (failure?.lockedUntil) {
          setError(formatLockMessage(failure.lockedUntil));
        } else {
          setError(GENERIC_ERROR);
        }
        return;
      }

      await clearLoginAttempts({
        data: { email: normalizedEmail, area: "cockpit" },
      }).catch(() => {});
      navigate({ to: search.redirect, replace: true });
    } finally {
      setBusy(false);
    }
  };

  const forgotSearch = (() => {
    const trimmed = email.trim().toLowerCase();
    return trimmed ? { email: trimmed } : {};
  })();


  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex h-16 max-w-6xl items-center px-6 md:px-12">
          <Logo />
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-3xl font-extrabold">Cockpit-Login</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Zugang für Kundinnen und Kunden der creategy GmbH.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" noValidate>
            <div>
              <label htmlFor="email" className="block text-sm font-semibold">
                E-Mail
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-semibold">
                Passwort
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
              />
            </div>

            <div className="space-y-2">
              <TurnstileWidget action="login"
                ref={turnstileRef}
                context="login"
                onToken={(token) => {
                  setTurnstileToken(token);
                  if (token) setTurnstileError(null);
                }}
                onError={(message) => setTurnstileError(message)}
              />
              {turnstileError && (
                <p role="alert" className="text-xs text-destructive">
                  {turnstileError}
                </p>
              )}
            </div>

            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            {info && !error && (
              <p className="text-sm text-muted-foreground">{info}</p>
            )}

            <button
              type="submit"
              disabled={busy || !turnstileToken}
              className="w-full rounded-md bg-brand-button px-4 py-2.5 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {busy ? "Einen Moment…" : "Anmelden"}
            </button>

            <Link
              to="/cockpit/passwort-vergessen"
              search={forgotSearch}
              className="block text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              Passwort vergessen?
            </Link>

          </form>
        </div>
      </main>
    </div>
  );
}
